/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/supportChat.ts
 *
 * Der Support-Chat von 121assist: welches Widget gilt und wie es auf die Seite
 * kommt und wieder weg. Rein, ohne React — der Lader dazu ist
 * `SupportChatLoader.tsx`, die Regeln der Konfiguration stehen in
 * `packages/core-py/support_chat.py`, das Ganze in
 * `docs/FLEET_SUPPORT_CHAT_SPEC.md`.
 *
 * Vertrag von `widget.js` (support-app, `backend/app/static/widget.js`):
 *
 *   <script src="{host}/widget.js" data-key="wpk_…" data-locale="de"></script>
 *
 * Das Widget liest Schlüssel und Sprache aus `document.currentScript`, legt
 * `window.SupportApp` an (`open`, `close`, `toggle`, `destroy`) und nimmt sich
 * mit `destroy()` vollständig von der Seite. `destroy()` gibt
 * `window.SupportApp` nur frei, wenn es noch die eigene Instanz ist.
 *
 * Die Entscheidungen:
 *  - **Zwei Sichten, zwei Widgets.** Gäste und Angemeldete haben je einen
 *    Schlüssel; ein leerer heisst „diese Gruppe sieht keinen Chat“.
 *  - **Nichts aus dem Konto.** Am Tag stehen nur Schlüssel und Sprache — keine
 *    Nutzer-ID, keine E-Mail, kein Name. 121assist kann eine Identität heute
 *    nicht prüfen; sie ungeprüft an einen fremden Host zu geben, wäre ein
 *    Abfluss ohne Zweck.
 *  - **Jede Einbettung baut genau ihre Instanz ab.** Sie merkt sich beim
 *    `load` ihres Scripts das `window.SupportApp`, das dieses Script angelegt
 *    hat, und ruft dessen `destroy()`. 121assist selbst hat auf derselben Seite
 *    eigene Widgets (Demo auf der Startseite, Test-Widget in der Verwaltung) —
 *    die bleiben unberührt.
 *  - **Eine Instanz je Seite.** Steht schon ein fremdes Widget auf der Seite
 *    (`window.SupportApp` vorhanden), bettet der Lader nichts ein und meldet es
 *    laut — zwei Chat-Knöpfe und ein überschriebenes `window.SupportApp` wären
 *    schlechter als keiner. Seiten mit eigenem Widget schalten den Lader ab.
 */

/** Host des Widgets für jede App ausser 121assist selbst (dort same-origin). */
export const ASSIST_HOST = 'https://www.121assist.com'

/** Vorgabe für `skriptUrl`. 121assist selbst übergibt `'/widget.js'`. */
export const ASSIST_WIDGET_URL = `${ASSIST_HOST}/widget.js`

/** Die öffentliche Route der App (`support_chat.OEFFENTLICHE_ROUTE`). */
export const SUPPORT_CHAT_KONFIG_URL = '/api/public/support-chat'

/**
 * Sprachen, die 121assist kann (`SUPPORTED_LOCALES` der support-app). Eine
 * andere Sprache gibt `widgetSprache` nicht weiter — `widget.js` fiele sonst
 * still auf die Browsersprache zurück.
 */
export const ASSIST_SPRACHEN = ['de', 'en', 'fr', 'it', 'es', 'nl', 'pl', 'sv', 'pt', 'no', 'da'] as const

/** Markiert das Script dieses Bausteins — fremde `widget.js`-Tags bleiben unberührt. */
export const SKRIPT_MARKE = 'data-fundament-support-chat'

/** Antwort von `GET /api/public/support-chat`. */
export interface SupportChatKonfig {
  enabled: boolean
  widget_key_anonymous: string | null
  widget_key_authenticated: string | null
}

/** Das globale Objekt, das `widget.js` anlegt. */
export interface SupportAppApi {
  open: () => void
  close: () => void
  toggle: () => void
  destroy: () => void
}

type MitSupportApp = { SupportApp?: SupportAppApi }

/** Prüft die Antwort der Route — eine falsche Form ist ein Fehler, kein „aus“. */
export function istSupportChatKonfig(wert: unknown): wert is SupportChatKonfig {
  if (typeof wert !== 'object' || wert === null) return false
  const k = wert as Record<string, unknown>
  const schluessel = (v: unknown) => v === null || typeof v === 'string'
  return (
    typeof k.enabled === 'boolean' &&
    schluessel(k.widget_key_anonymous) &&
    schluessel(k.widget_key_authenticated)
  )
}

/** Der Schlüssel für Gäste bzw. Angemeldete, oder `null` (kein Chat). */
export function supportChatSchluessel(konfig: SupportChatKonfig, angemeldet: boolean): string | null {
  if (!konfig.enabled) return null
  return (angemeldet ? konfig.widget_key_authenticated : konfig.widget_key_anonymous) || null
}

/**
 * Sprache der Seite → `data-locale`, oder `null`, wenn 121assist sie nicht kann.
 * `de-AT` und `DE` werden zu `de` — so liest `widget.js` das Attribut auch.
 */
export function widgetSprache(sprache: string): string | null {
  const basis = sprache.trim().toLowerCase().split(/[-_]/)[0]
  return (ASSIST_SPRACHEN as readonly string[]).includes(basis) ? basis : null
}

/** Was eingebettet werden soll. */
export interface WidgetZiel {
  src: string
  key: string
  /** `null`: kein `data-locale` (das Widget nimmt die Browsersprache). */
  locale: string | null
}

export function gleichesZiel(a: WidgetZiel | null, b: WidgetZiel | null): boolean {
  if (a === null || b === null) return a === b
  return a.src === b.src && a.key === b.key && a.locale === b.locale
}

/** Ein eingebettetes Widget. `abbauen()` ist idempotent. */
export interface Einbettung {
  ziel: WidgetZiel
  abbauen: () => void
}

/**
 * Setzt das Widget-Script ein und gibt die Einbettung zurück, oder `null`,
 * wenn schon ein fremdes Widget auf der Seite steht (laut gemeldet).
 */
export function einbetten(ziel: WidgetZiel): Einbettung | null {
  const fenster = window as unknown as MitSupportApp
  if (fenster.SupportApp) {
    console.error(
      '[support-chat] Auf dieser Seite läuft schon ein Widget (window.SupportApp) — kein zweites. ' +
        'Seiten mit eigenem Widget schalten den Lader mit aktiv={false} ab.',
    )
    return null
  }

  let instanz: SupportAppApi | null = null
  let abgebaut = false

  const script = document.createElement('script')
  script.src = ziel.src
  // Dynamisch eingefügte Scripts laufen sonst in Ankunftsreihenfolge. Wird ein
  // Widget abgebaut, bevor sein Script lief, und sofort ein neues eingesetzt,
  // muss das alte zuerst laufen — sonst überschriebe es `window.SupportApp`
  // des neuen. `async = false` hält die Einfügereihenfolge, ohne das Parsen
  // zu blockieren.
  script.async = false
  script.setAttribute(SKRIPT_MARKE, '1')
  script.setAttribute('data-key', ziel.key)
  if (ziel.locale) script.setAttribute('data-locale', ziel.locale)

  script.addEventListener('load', () => {
    // `load` folgt unmittelbar auf das Ausführen dieses Scripts — das
    // `window.SupportApp` jetzt ist das, das es angelegt hat.
    const api = fenster.SupportApp
    if (!api || typeof api.destroy !== 'function') {
      console.error('[support-chat] widget.js geladen, aber kein window.SupportApp — kein Chat.', ziel.src)
      return
    }
    instanz = api
    // Schon abgebaut, bevor das Script lief (entfernen hält ein geladenes
    // Script nicht auf): gleich wieder weg.
    if (abgebaut) api.destroy()
  })
  script.addEventListener('error', () => {
    console.error('[support-chat] widget.js nicht ladbar — CSP oder Host prüfen:', ziel.src)
  })
  document.body.appendChild(script)

  return {
    ziel,
    abbauen() {
      if (abgebaut) return
      abgebaut = true
      // Das Widget hängt Knopf, Fenster und Stil an die Seite; nur das Script zu
      // entfernen, liesse sie stehen.
      instanz?.destroy()
      script.remove()
    },
  }
}
